/**
 * Utility functions for exporting parcel comparison data to PDF
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Helper function to get capacity value for display
 */
const getCapacityDisplay = (parcel) => {
    if (Array.isArray(parcel.nearby_circuits) && parcel.nearby_circuits.length > 0) {
        const capacities = parcel.nearby_circuits
            .map(circuit => circuit.capacity_kw)
            .filter(cap => cap != null);
        if (capacities.length > 0) {
            return `${Math.max(...capacities)} kW`;
        }
    } else if (parcel.nearby_circuits_ladwp != null && parcel.nearby_circuits_ladwp !== 'N/A') {
        if (Array.isArray(parcel.nearby_circuits_ladwp)) {
            const capacities = parcel.nearby_circuits_ladwp.filter(c => c.capacity_kw);
            if (capacities.length > 0) {
                // Sort and get highest
                capacities.sort((a, b) => {
                    const aMatch = a.capacity_kw?.match(/(\d+)/);
                    const bMatch = b.capacity_kw?.match(/(\d+)/);
                    const aVal = aMatch ? parseInt(aMatch[1]) : -1;
                    const bVal = bMatch ? parseInt(bMatch[1]) : -1;
                    return bVal - aVal;
                });
                return capacities[0].capacity_kw;
            }
        } else if (typeof parcel.nearby_circuits_ladwp === 'object') {
            return parcel.nearby_circuits_ladwp.capacity_kw ?? 'N/A';
        }
    }
    return 'N/A';
};

/**
 * Helper function to get circuit information
 */
const getCircuitInfo = (parcel) => {
    if (Array.isArray(parcel.nearby_circuits) && parcel.nearby_circuits.length > 0) {
        return parcel.nearby_circuits.slice(0, 3).map(circuit => ({
            name: circuit.circuit_name || 'N/A',
            capacity: `${circuit.capacity_kw || 'N/A'} kW`,
            distance: circuit.distance_miles ? `${circuit.distance_miles.toFixed(2)} mi` : 'N/A',
            voltage: circuit.circuit_voltage ? `${circuit.circuit_voltage} kV` : 'N/A',
            substation: circuit.substation_name || 'N/A'
        }));
    } else if (parcel.nearby_circuits_ladwp != null && parcel.nearby_circuits_ladwp !== 'N/A') {
        const circuits = Array.isArray(parcel.nearby_circuits_ladwp) 
            ? parcel.nearby_circuits_ladwp 
            : [parcel.nearby_circuits_ladwp];
        
        return circuits.slice(0, 3).map(circuit => ({
            name: circuit.circuit_name || circuit.circuit_id || 'N/A',
            capacity: circuit.capacity_kw || 'N/A',
            distance: circuit.distance_miles ? `${circuit.distance_miles.toFixed(2)} mi` : 'N/A',
            voltage: circuit.circuit_voltage ? `${circuit.circuit_voltage} kV` : 'N/A',
            substation: circuit.substation_name || 'N/A'
        }));
    }
    return [];
};

/**
 * Export parcels comparison to PDF with detailed reports for top 5 recommended
 */
export const exportParcelsToPDF = (parcels) => {
    if (!parcels || parcels.length === 0) {
        alert('No parcels to export');
        return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Helper to add new page if needed
    const checkPageBreak = (neededSpace = 20) => {
        if (yPosition + neededSpace > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
            return true;
        }
        return false;
    };

    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Land Parcel Comparison Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Date
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const dateStr = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });
    doc.text(`Generated: ${dateStr}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Sort parcels by recommendation score
    const sortedParcels = [...parcels].sort((a, b) => 
        (b.recommendation_score || 0) - (a.recommendation_score || 0)
    );

    // Section 1: Top 5 Recommended Parcels - Detailed Reports
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Top 5 Recommended Parcels - Detailed Analysis', 14, yPosition);
    yPosition += 10;

    const top5Parcels = sortedParcels.slice(0, 5);

    top5Parcels.forEach((parcel, index) => {
        checkPageBreak(80);

        // Parcel header
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(41, 128, 185); // Blue color
        doc.text(`#${index + 1} - ${parcel.Address || 'N/A'}`, 14, yPosition);
        yPosition += 7;

        // Recommendation score
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');

        // Basic Information
        const basicInfo = [
            ['Property Details', ''],
            ['APN', parcel.APN || 'N/A'],
            ['Address', parcel.Address || 'N/A'],
            ['ZIP Code', parcel.Zip5 || parcel.Zip || 'N/A'],
            ['Land Value', parcel.Land_Value ? `$${parseInt(parcel.Land_Value).toLocaleString()}` : 'N/A'],
            ['Area', parcel.Area ? `${parcel.Area.toFixed(2)} sq ft` : 'N/A'],
            ['', ''],
            ['Zoning & Jurisdiction', ''],
            ['Zone Classification', parcel.zone_cla_1 || 'N/A'],
            ['Zone Description', parcel.zone_Descr || 'N/A'],
            ['Jurisdiction', parcel.zone_Juris || 'N/A'],
            ['', ''],
            ['Utility Information', ''],
            ['Utility Region', parcel.utility_region_acronym || 'N/A'],
            ['Max Capacity', getCapacityDisplay(parcel)],
        ];

        doc.autoTable({
            startY: yPosition,
            head: [],
            body: basicInfo,
            theme: 'plain',
            styles: { 
                fontSize: 9,
                cellPadding: 2,
            },
            columnStyles: {
                0: { fontStyle: 'bold', cellWidth: 50 },
                1: { cellWidth: 130 }
            },
            didParseCell: function(data) {
                if (data.row.index === 0 || data.row.index === 8 || data.row.index === 13) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fontSize = 10;
                    data.cell.styles.textColor = [41, 128, 185];
                }
            }
        });

        yPosition = doc.lastAutoTable.finalY + 7;

        // Nearby Circuits
        const circuits = getCircuitInfo(parcel);
        if (circuits.length > 0) {
            checkPageBreak(40);
            
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(41, 128, 185);
            doc.text('Nearby Circuits (Top 3)', 14, yPosition);
            yPosition += 5;

            const circuitData = circuits.map((circuit, idx) => [
                `Circuit ${idx + 1}`,
                circuit.name,
                circuit.capacity,
                circuit.distance,
                circuit.voltage,
                circuit.substation
            ]);

            doc.autoTable({
                startY: yPosition,
                head: [['', 'Circuit Name', 'Capacity', 'Distance', 'Voltage', 'Substation']],
                body: circuitData,
                theme: 'striped',
                styles: { fontSize: 8 },
                headStyles: { fillColor: [52, 152, 219] },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 35 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 22 },
                    4: { cellWidth: 22 },
                    5: { cellWidth: 40 }
                }
            });

            yPosition = doc.lastAutoTable.finalY + 10;
        } else {
            yPosition += 5;
        }

        // Add separator line
        if (index < top5Parcels.length - 1) {
            checkPageBreak(15);
            doc.setDrawColor(200, 200, 200);
            doc.line(14, yPosition, pageWidth - 14, yPosition);
            yPosition += 10;
        }
    });

    // Section 2: All Selected Parcels Summary
    doc.addPage();
    yPosition = 20;

    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(`All Selected Parcels (${parcels.length} total)`, 14, yPosition);
    
    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Parcels are listed in descending order based on recommended capacity.', 14, yPosition);
    
    yPosition += 10;

    // Create summary table with all parcels
    const summaryData = sortedParcels.map((parcel, index) => [
        index + 1,
        parcel.Address || 'N/A',
        parcel.APN || 'N/A',
        parcel.Land_Value ? `$${parseInt(parcel.Land_Value).toLocaleString()}` : 'N/A',
        parcel.Area ? `${parcel.Area.toFixed(0)}` : 'N/A',
        parcel.zone_cla_1 || 'N/A',
        parcel.utility_region_acronym || 'N/A',
        getCapacityDisplay(parcel),
        parcel.recommendation_score?.toFixed(2) || 'N/A'
    ]);

    doc.autoTable({
        startY: yPosition,
        head: [['#', 'Address', 'APN', 'Land Value', 'Area (sqft)', 'Zone', 'Utility', 'Capacity', 'Score']],
        body: summaryData,
        theme: 'striped',
        styles: { 
            fontSize: 8,
            cellPadding: 2
        },
        headStyles: { 
            fillColor: [52, 152, 219],
            fontStyle: 'bold'
        },
        columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 40 },
            2: { cellWidth: 25 },
            3: { cellWidth: 25 },
            4: { cellWidth: 20 },
            5: { cellWidth: 15 },
            6: { cellWidth: 20 },
            7: { cellWidth: 20 },
            8: { cellWidth: 15 }
        },
        didParseCell: function(data) {
            // Highlight top 5
            if (data.row.index < 5 && data.section === 'body') {
                data.cell.styles.fillColor = [241, 248, 233]; // Light green
            }
        }
    });

    // Footer on last page
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
            `Page ${i} of ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
        );
        doc.text(
            'AmpTrans - Charging Station Site Analysis',
            pageWidth - 14,
            pageHeight - 10,
            { align: 'right' }
        );
    }

    // Save the PDF
    const fileName = `Parcel_Comparison_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
};

